import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { ReactNode } from "react";

import { fetchMyAccounts } from "../data/accounts";
import type { LiveConfig } from "../lib/live-config";
import { BrandMark } from "../components/BrandMark";
import { extractPermissionScope } from "../nav/usePermissions";
import { useAuth } from "./AuthProvider";
import { ConsoleAccessBlocked } from "./ConsoleAccessBlocked";
import { isConsoleEligible } from "./console-access";

type GateStatus = "loading" | "allowed" | "blocked" | "error";

/** Post-auth gate: guard-only memberships never reach the console shell. */
export function ConsoleAccessGate({
  client,
  config,
  children,
}: {
  client: SupabaseClient;
  config: LiveConfig;
  children: ReactNode;
}) {
  const { signOut } = useAuth();
  const [status, setStatus] = useState<GateStatus>("loading");

  useEffect(() => {
    let active = true;

    async function verifyAccess(): Promise<void> {
      try {
        const accounts = await fetchMyAccounts(client);
        if (!active) return;
        if (isConsoleEligible(accounts)) {
          setStatus("allowed");
          return;
        }
        if (await hasPlatformSuperadminAccess(client, config.accountId)) {
          setStatus("allowed");
          return;
        }
        setStatus("blocked");
      } catch {
        if (!active) return;
        setStatus("error");
      }
    }

    void verifyAccess();

    return () => {
      active = false;
    };
  }, [client, config.accountId]);

  if (status === "loading") {
    return <BootScreen subtitle="Checking console access…" />;
  }
  if (status === "error") {
    return (
      <BootScreen
        subtitle="Could not verify console access."
        action={
          <button className="signin__submit" type="button" onClick={() => void signOut()}>
            Sign out
          </button>
        }
      />
    );
  }
  if (status === "blocked") {
    return <ConsoleAccessBlocked onSignOut={() => void signOut()} />;
  }
  return children;
}

async function hasPlatformSuperadminAccess(client: SupabaseClient, accountId: string): Promise<boolean> {
  const { data, error } = await client.rpc("current_account_permissions", { target_account_id: accountId });
  if (error !== null) {
    return false;
  }
  return extractPermissionScope(data).isSuperadmin;
}

function BootScreen({ subtitle, action }: { subtitle: string; action?: ReactNode }) {
  return (
    <div className="boot">
      <main className="boot__card" role="main" aria-labelledby="boot-title">
        <BrandMark className="boot__mark" />
        <h1 id="boot-title" className="boot__title">
          SecOps Command Center
        </h1>
        <p className="boot__tagline">{subtitle}</p>
        {action}
      </main>
    </div>
  );
}
